import React, { useState, useMemo } from 'react'
import { Col, Row, DropdownButton, Dropdown } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PageTitle } from 'domain/feature/hub/pageTitle/styles'
import { showConfirmationPopUp } from 'components/popup/confirmationModal'
import { showPublicOrganInfoModal } from 'components/popup/publicOrganInfoModal'
import { showToast } from 'components/toast'
import { PublicOrganInfoModal } from './PublicOrganInfoModal'
import { Input, BaseButton } from 'iebt-zing-storybook'
import { trans } from 'sharedKernel/i18n'
import { Filter } from 'components/Filter'
import { postProvider, PostFormat, GetFormat, useGet, deleteProvider, DeleteFormat } from 'sharedKernel/api'
import { v4 as uuidv4 } from 'uuid'
import { Loading } from 'components/loading'
import styled from 'styled-components'

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

const HeaderWrapper = styled.div`
  margin-bottom: 16px;
`

const AddEmailWrapper = styled.div`
  margin-bottom: 20px;
`

const EmailButtonWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  
  .email-input {
    flex: 1;
  }
  
  .add-button {
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    
    .add-button {
      margin-top: 8px;
    }
  }
`

const SearchRow = styled(Row)`
  margin: 12px 0 20px 0;
`

const FilterWrapper = styled.div`
  .input-group {
    height: 28px;
  }
  
  .form-control {
    height: 28px;
    font-size: 0.8rem;
    padding: 0.25rem 0.5rem;
  }
  
  .input-group-text {
    height: 28px;
    padding: 0.25rem 0.5rem;
  }
`

const TableContainer = styled.div`
  background: #ffffff;
  border: 1px solid #0f73b0;
  border-radius: 4px;
  overflow: hidden;
`

const TableHead = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 120px;
  background: var(--white);
  color: var(--primary);
  font-weight: 400;
  font-size: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--primary);
`

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 120px;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  
  &:last-child {
    border-bottom: none;
  }
`

const RowValue = styled.span`
  font-size: 14px;
  color: #1f1f1f;
`

const StatusText = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 16px;
  line-height: 140%;
  letter-spacing: 0%;
`

const ActionsDropdownButton = styled(DropdownButton)`
  && {
    width: 32px;
    height: 32px;
    padding: 0;
    background: transparent !important;
    border: 1px solid #0066B3 !important;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    button {
      width: 100%;
      height: 100%;
      background: transparent !important;
      border: none !important;
      color: #0066B3 !important;
      padding: 0;
      box-shadow: none !important;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      
      &::after {
        display: none !important;
      }
      
      &:hover, &:focus, &:active {
        background: transparent !important;
        border: none !important;
        color: #0066B3 !important;
        box-shadow: none !important;
      }
    }
    
    &:hover, &:focus, &:active {
      background: transparent !important;
      border-color: #0066B3 !important;
    }
  }
`

interface IPublicOrgan {
  id: string
  name: string
  status: 'Cadastrado' | 'Não cadastrado'
  representativeEmail?: string
  mayorName?: string
  representativeName?: string
  mobile?: string
  cityHallId: string
  numberOfPeople?: number
  teamSize?: number
  hasSecretariat?: boolean
  hasSecretary?: boolean
  secretariatName?: string
  secretary?: string
  secretaryResponsible?: string
  createdAt?: string
  updatedAt?: string
}

interface ICityHallResponse {
  data: IPublicOrgan[]
}

export const HubCompanyPublicOrgansManagement = (): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState('')
  const [representativeName, setRepresentativeName] = useState('')
  const [emailValue, setEmailValue] = useState('')
  const { mutate: sendInvite } = postProvider()

  const buildCityHallsUrl = (): string => {
    if (searchTerm.trim()) {
      return `/v1/hub/company/city-hall-admins?search=${encodeURIComponent(searchTerm.trim())}`
    }
    return '/v1/hub/company/city-hall-admins'
  }

  const cityHallsRequest: GetFormat = {
    url: buildCityHallsUrl()
  }

  const { data: cityHallsData, isFetching, refetch } = useGet<ICityHallResponse>(
    cityHallsRequest,
    `city-halls-${searchTerm}`
  )

  const publicOrgans = useMemo<IPublicOrgan[]>(() => {
    if (!cityHallsData?.data) return []
    
    return cityHallsData.data.map((cityHall: any) => ({
      id: cityHall.id,
      cityHallId: cityHall.cityHallId,
      name: cityHall.cityHallName || cityHall.name || 'Prefeitura',
      status: cityHall.status === 1 ? 'Cadastrado' : 'Convidado',
      representativeEmail: cityHall.email || cityHall.representativeEmail || '', 
      representativeName: cityHall.name || cityHall.representativeName || '',
      mayorName: cityHall.mayorName || '',
      numberOfPeople: cityHall.teamSize || cityHall.numberOfPeople || 0,
      hasSecretariat: cityHall.hasSecretary !== undefined ? cityHall.hasSecretary : (cityHall.hasSecretariat !== undefined ? cityHall.hasSecretariat : false),
      secretariatName: cityHall.secretary || cityHall.secretariatName || '',
      secretaryResponsible: cityHall.secretaryResponsible || '',
      createdAt: cityHall.createdAt,
      updatedAt: cityHall.updatedAt,
      mobile: cityHall.mobile || cityHall.representativePhone || ''
    }))
  }, [cityHallsData])

  const handleAddEmail = (): void => {
    if (!representativeName.trim() || !emailValue.trim()) {
      showToast('error', 'Preencha o nome do representante e o e-mail')
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(emailValue.trim())) {
      showToast('error', 'E-mail inválido')
      return
    }

    const cityHallId = EMPTY_GUID
    const inviteUrl = `/v1/hub/company/invite-city-hall`
    
    const invitePost: PostFormat = {
      url: inviteUrl,
      data: {
        id: uuidv4(),
        data: {
          cityHallId: cityHallId,
          email: emailValue.trim(),
          name: representativeName.trim(),
          position: 'admin'
        }
      }
    }

    sendInvite(invitePost, {
      onSuccess: () => {
        showToast('success', 'Convite enviado com sucesso!')
        setRepresentativeName('')
        setEmailValue('')
        refetch()
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao enviar convite'
        showToast('error', errorMessage)
      }
    })
  }

  const handleNotify = async (organ: IPublicOrgan): Promise<void> => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 500))
      showToast('success', 'Notificação reenviada com sucesso!')
    } catch (error) {
      showToast('error', 'Erro ao reenviar notificação')
    }
  }

  const onSuccessDeletion = async (): Promise<void> => {
    showToast('success', 'Prefeitura removida com sucesso!')
    refetch()
  }

  const { mutate: deleteCityHall } = deleteProvider(onSuccessDeletion)

  const handleRemove = (member: IPublicOrgan): void => {
    const request: DeleteFormat = {
      url: `/v1/system/user/admin-city-hall/${member.cityHallId}`,
    }

    showConfirmationPopUp({
      title: 'Remover Prefeitura',
      message: (
        <span>
          Tem certeza de que deseja remover <strong>{member.name}</strong>?
        </span>
      ),
      onConfirm: () => {
        deleteCityHall(request)
      },
    })
  }

  const handleViewDetails = (organ: IPublicOrgan): void => {
    const organForModal = {
      ...organ,
      representativeEmail: organ.representativeEmail || ''
    }
    
    showPublicOrganInfoModal({
      title: organ.name,
      component: <PublicOrganInfoModal organ={organForModal as any} />,
    })
  }

  const filteredOrgans = publicOrgans

  return (
    <>
      <HeaderWrapper>
        <Row className="align-items-center">
          <Col>
            <PageTitle>Gerenciar prefeituras</PageTitle>
          </Col>
        </Row>
      </HeaderWrapper>

      <AddEmailWrapper>
        <Row className="align-items-end">
          <Col xs={12} md={6}>
            <Input
              id="representativeName"
              isRequired={true}
              value={representativeName}
              label={trans('Nome da prefeitura')}
              onChange={(event) => {
                setRepresentativeName(event.target.value)
              }}
            />
          </Col>
          <Col xs={12} md={6}>
            <EmailButtonWrapper>
              <div className="email-input">
                <Input
                  id="representativeEmail"
                  isRequired={true}
                  value={emailValue}
                  label={trans('E-mail do representante:')}
                  onChange={(event) => {
                    setEmailValue(event.target.value.split(' ').join(''))
                  }}
                />
              </div>
              <div className="add-button">
                <BaseButton
                  disabled={!representativeName.trim() || !emailValue.trim()}
                  btnContent={trans('Adicionar')}
                  variant="primary"
                  size="medium"
                  onClick={handleAddEmail}
                />
              </div>
            </EmailButtonWrapper>
          </Col>
        </Row>
      </AddEmailWrapper>

      <SearchRow>
        <Col md={6}>
          <FilterWrapper>
            <Filter
              currentSearchTerm={searchTerm}
              btnClick={(term: string) => setSearchTerm(term)}
              onFilterChange={(term: string) => setSearchTerm(term)}
            />
          </FilterWrapper>
        </Col>
      </SearchRow>

      {isFetching ? (
        <div className="d-flex justify-content-center py-5">
          <Loading />
        </div>
      ) : (
        <TableContainer>
          <TableHead>
            <div>Prefeitura</div>
            <div>Status</div>
            <div style={{ textAlign: 'right' }}>Ações</div>
          </TableHead>
          {filteredOrgans.length > 0 ? (
            filteredOrgans.map((organ) => (
              <TableRow key={organ.id}>
                <RowValue
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => handleViewDetails(organ)}
                >
                  {organ.name}
                </RowValue>
                <StatusText>{organ.status}</StatusText>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <ActionsDropdownButton
                    id={`dropdown-actions-${organ.id}`}
                    title={<FontAwesomeIcon icon={['fas', 'ellipsis-vertical']} style={{ color: '#0066B3' }} />}
                    variant="link"
                  >
                    {organ.status !== 'Cadastrado' && (
                      <Dropdown.Item onClick={() => handleNotify(organ)}>
                        <FontAwesomeIcon icon={['fas', 'envelope']} className="me-2" />
                        Notificar novamente
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item
                      onClick={() => handleRemove(organ)}
                    >
                      <FontAwesomeIcon icon={['fas', 'trash']} className="me-2" />
                      Remover
                    </Dropdown.Item>
                  </ActionsDropdownButton>
                </div>
              </TableRow>
            ))
          ) : (
            <TableRow style={{ display: 'block', padding: '40px', textAlign: 'center' }}>
              <div>Ainda não há organizações públicas cadastradas</div>
            </TableRow>
          )}
        </TableContainer>
      )}
    </>
  )
}

